import { serviceURLs } from '@leshen/gatsby-theme-leshen/environment-config'
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'
import AddressQualification from '../components/Form/AddressQualification'
import getFullStateName from '../utils/getFullStateName'

export const UserContext = createContext()
export const useUser = () => useContext(UserContext)

function makeMapiLocation(data = {}, fetchResponse = {}) {
  return {
    error: false,
    mapiResponse: fetchResponse,
    address: data.address || null,
    zipCode: data.zip_code || null,
    city: data.city_mixed_case || null,
    state: {
      fullName: data.state_full_name || null,
      abbreviation: data.state || null,
    },
  }
}

async function getGeoZipCode() {
  const result = await fetch('https://m.clear.link/geoip')
    .then((res) => (res?.ok ? res.json() : null))
    .catch(() => null)

  return result?.data?.cityPostalCode.length === 5
    ? result?.data?.cityPostalCode
    : null
}

async function getLocation() {
  let location = makeMapiLocation()
  let zip = new URLSearchParams(window.location.search).get('zip')

  /** If we don't have a zip code, but we do have the cached location */
  const storage = window.localStorage.getItem('user-location')
  const cache = storage ? JSON.parse(storage) || {} : {}
  if (!zip && cache.zipCode && cache.state?.fullName && cache.city) {
    return cache
  }

  /** Get zip code by geoip if no cached location */
  if (!zip) {
    zip = await getGeoZipCode()
  }

  if (zip) {
    let apiRes = {}
    await fetch(`${serviceURLs.mapiZipURL}${zip}`)
      .then((res) => {
        apiRes = res
        return res
      })
      .then((res) => (res?.ok ? res.json() : {}))
      .then((resData) => {
        const data = resData?.data?.zipCodes?.find(
          (entry) => entry.primary_record === 'P'
        )
        location = makeMapiLocation(data, apiRes)
      })
      .catch((err) => {
        location.error = err
      })
  }

  return location
}

/**
 * @param {Object} props
 * @param {Object} props.address
 * @param {string} props.smartyStreetsWebsiteKey
 * @param {string} props.addressQualificationCode,
 */
async function getAddressQualificaiton({
  address,
  smartyStreetsWebsiteKey,
  addressQualificationCode,
}) {
  const addressQual = new AddressQualification({
    addressQualificationCode,
    smartyStreetsWebsiteKey,
  })
  const requestId = await window?.mapiUtils?.getRequestId()
  if (!requestId) return null
  return addressQual.checkForService(address, requestId).catch((err) => err)
}

export function UserProvider({ children }) {
  const [userLocation, setUserLocation] = useState(makeMapiLocation())
  const [isLoading, setIsLoading] = useState(false)
  const [userData, setUserData] = useState(null)
  const [serviceData, setServiceData] = useState(null)
  const isClient = typeof window !== 'undefined'

  const setAddressLocation = useCallback(
    (params) => {
      setIsLoading(true)
      getAddressQualificaiton(params)
        .then((result) => {
          setServiceData(result.serviceData)
          setUserLocation({
            address: params.address || null,
            zipCode: result?.address?.postal_code || null,
            city: result?.address?.locality || null,
            state: {
              fullName: getFullStateName(result?.address?.administrative_area),
              abbreviation: result.address.administrative_area,
            },
          })
        })
        .finally(() => setIsLoading(false))
    },
    [setIsLoading, setUserLocation]
  )

  const setZipLocation = useCallback(() => {
    setIsLoading(true)
    getLocation()
      .then((loc) => setUserLocation(loc))
      .finally(() => setIsLoading(false))
  }, [setIsLoading, setUserLocation])

  useEffect(() => {
    if (isClient) setZipLocation()
  }, [isClient, setZipLocation])

  return (
    <UserContext.Provider
      value={{
        userLocation,
        setAddressLocation,
        setZipLocation,
        isLoading,
        userData,
        setUserData,
        serviceData,
      }}
    >
      {children}
    </UserContext.Provider>
  )
}
